import { useState, useEffect } from "react";
import { useParams, Link } from "react-router";
import { motion } from "motion/react";
import { Calendar, Clock, ArrowLeft, Tag, User, Loader2, ImageIcon } from "lucide-react";
import { blogService, type BlogPost } from "../../lib/services/blogService";
import type { Models } from "appwrite";

type BlogDoc = BlogPost & Models.Document;

const FALLBACK_IMG = "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=600";

import { SEO } from "./SEO";

export function BlogDetailPage() {
    const { slug } = useParams();
    const [post, setPost] = useState<BlogDoc | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Fetch all blogs, find by slug
        blogService.list()
            .then((docs) => {
                // Find the matching published post
                const found = docs.find(p => p.slug === slug && p.status !== "Draft");
                setPost(found || null);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#1a2d50]/40 mb-4" />
                <p className="text-[#1a2d50] font-medium">Loading Article...</p>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
                <h1 className="text-3xl text-[#1a2d50] mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                    Article Not Found
                </h1>
                <p className="text-muted-foreground mb-8">This post doesn't exist or may have been removed.</p>
                <Link to="/blog" className="inline-flex items-center gap-2 bg-[#E9A319] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#d4920f] transition-all">
                    <ArrowLeft className="w-4 h-4" /> Back to Blog
                </Link>
            </div>
        );
    }

    return (
        <article className="pb-24">
            <SEO
                title={`${post.title} | Solace SMART Solutions`}
                description={post.excerpt || post.content?.substring(0, 160).replace(/<[^>]*>/g, "")}
                ogImage={post.imageUrl}
                ogType="article"
            />
            {/* Hero */}
            <section className="relative pt-24 pb-16 bg-[#1a2d50] text-white overflow-hidden">
                <div className="absolute inset-0">
                    {post.imageUrl ? (
                        <img src={post.imageUrl} alt={post.title} className="w-full h-full object-cover opacity-20" onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_IMG; }} />
                    ) : (
                        <div className="w-full h-full bg-[#1a2d50]" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a2d50] to-[#1a2d50]/60" />
                </div>

                <div className="max-w-4xl mx-auto px-6 relative z-10">
                    <Link to="/blog" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm mb-8">
                        <ArrowLeft className="w-4 h-4" /> Back to all articles
                    </Link>

                    <div className="flex flex-wrap items-center gap-4 mb-6 text-sm">
                        <span className="bg-[#E9A319] text-white px-3 py-1 rounded-full font-semibold flex items-center gap-1.5">
                            <Tag className="w-3.5 h-3.5" /> {post.category}
                        </span>
                        <span className="flex items-center gap-1.5 text-white/80"><Calendar className="w-4 h-4" /> {post.date}</span>
                        <span className="flex items-center gap-1.5 text-white/80"><Clock className="w-4 h-4" /> {post.readTime}</span>
                    </div>

                    <h1 className="text-4xl md:text-5xl lg:text-6xl text-white mb-6 leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                        {post.title}
                    </h1>

                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center border border-white/20">
                            <User className="w-5 h-5 text-white/80" />
                        </div>
                        <div>
                            <p className="font-semibold text-white">{post.author}</p>
                            <p className="text-xs text-white/60">Solace Smart Solutions</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="max-w-4xl mx-auto px-6 py-12">
                <div className="bg-white rounded-3xl -mt-24 relative z-20 border border-gray-100 p-8 md:p-12">
                    {post.excerpt && (
                        <p className="text-xl md:text-2xl text-[#1a2d50] font-medium leading-relaxed mb-10 pb-10 border-b border-gray-100" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                            {post.excerpt}
                        </p>
                    )}

                    <div className="prose prose-lg prose-blue max-w-none text-muted-foreground" style={{ lineHeight: 1.8 }}>
                        {!post.content ? (
                            <p>Content is missing for this article.</p>
                        ) : (
                            <div dangerouslySetInnerHTML={{ __html: post.content }} />
                        )}
                    </div>
                </div>
            </section>
        </article>
    );
}
